import { useEffect, useSyncExternalStore } from "react";
import { ADMIN_MENU_ITEMS, COMPANY_MENU_ITEMS, PROJECT_MENU_ITEMS } from "@shared/navigationMenu";

/**
 * "New" and "Updated" mean new TO THE READER.
 *
 * Platform Settings → General puts a chip on a menu item, and every reader saw
 * it on every visit for as long as it stayed set: Platform Settings opened on
 * six "New" rows at once (QA, 2026-10-28), which says nothing about what is
 * new to the person reading. So a `new` / `updated` chip is drawn until the
 * reader has opened that item once since the chip was set, and then not
 * again. Setting the chip afresh (a new `updatedAt`) shows it again.
 *
 * Coming Soon is state, not news: it is always drawn.
 *
 * Per device, in localStorage — a convenience. An empty or unreadable store
 * just means every chip is drawn, which is how it was before.
 */

const KEY = "salefish:menu-chips-seen:v1";

export interface MenuChipSetting {
  menuKey?: string;
  chip?: string | null;
  updatedAt?: string | null;
}

type Seen = Record<string, string>;

let cache: Seen | null = null;
const listeners = new Set<() => void>();

function read(): Seen {
  if (cache) return cache;
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    cache = parsed && typeof parsed === "object" ? (parsed as Seen) : {};
  } catch {
    cache = {};
  }
  return cache;
}

/** What the reader saw: the chip, and when it was set. */
export function menuChipStamp(chip: string | null | undefined, updatedAt?: string | null): string {
  return `${chip ?? ""}@${updatedAt ?? ""}`;
}

/** Pure: the chip to draw, given what this reader has already opened. */
export function chipToShow(
  menuKey: string,
  setting: MenuChipSetting | null | undefined,
  seen: Seen,
): string | null {
  const chip = setting?.chip ?? null;
  if (chip !== "new" && chip !== "updated") return chip;
  return seen[menuKey] === menuChipStamp(chip, setting?.updatedAt) ? null : chip;
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) {
      cache = null;
      fn();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(fn);
    window.removeEventListener("storage", onStorage);
  };
}

export function markMenuChipSeen(menuKey: string, setting: MenuChipSetting | null | undefined) {
  const chip = setting?.chip ?? null;
  if (chip !== "new" && chip !== "updated") return;
  const stamp = menuChipStamp(chip, setting?.updatedAt);
  const seen = read();
  if (seen[menuKey] === stamp) return;
  cache = { ...seen, [menuKey]: stamp };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(cache));
  } catch {
    /* private mode: remembered for this page load only */
  }
  listeners.forEach((l) => l());
}

/** The reader's seen chips, re-rendering when one is marked. */
export function useSeenMenuChips(): Seen {
  return useSyncExternalStore(subscribe, read, () => ({}));
}

/** Which menu item a path is on, in any area. */
export function menuKeyForPath(path: string): string | null {
  const clean = path.split("?")[0].replace(/\/$/, "");
  const under = (href: string) => clean === href || clean.startsWith(href + "/");
  // The longest match wins: Company details is `/company-admin` itself, and
  // every other company section sits under it.
  let best: { key: string; len: number } | null = null;
  for (const item of [...ADMIN_MENU_ITEMS, ...COMPANY_MENU_ITEMS]) {
    if (!item.url.startsWith("/") || !under(item.url)) continue;
    if (!best || item.url.length > best.len) best = { key: item.key, len: item.url.length };
  }
  if (best) return best.key;
  const m = /^\/projects\/[^/]+\/(.+)$/.exec(clean);
  if (m) {
    const rest = m[1];
    for (const item of PROJECT_MENU_ITEMS) {
      const urls = [item.url, ...(item.children ?? []).map((c) => c.url)];
      if (urls.some((u) => rest === u || rest.startsWith(u + "/"))) return item.key;
    }
  }
  return null;
}

/**
 * Marks the open page's menu item as seen. Mounted once, in AppShell, so it
 * runs on every page in every area.
 */
export function useMarkOpenMenuChipSeen(location: string, settings: MenuChipSetting[] | undefined) {
  useEffect(() => {
    if (!settings) return;
    const key = menuKeyForPath(location);
    if (!key) return;
    markMenuChipSeen(key, settings.find((s) => s.menuKey === key));
  }, [location, settings]);
}
